import { HEADING_STEPS } from "../sim/fixed";
import { isKnockDownClip, isStandUpClip, type ClipName } from "./creaturemesh";

/** `NRaceData::EHitType` (`GameType.h:44-49`). */
export const HIT_TYPE_NONE = 0;
export const HIT_TYPE_GREAT = 1;
export const HIT_TYPE_GOOD = 2;

/** The victim's side of one landed blow: the attacker's clip's hit type, both
 *  bodies' headings, what the victim is playing and which reaction clips its
 *  race was converted with. */
export interface HitReactionInput {
  readonly hittingType: number | undefined;
  readonly attackerHeading: number;
  readonly victimHeading: number;
  readonly playing: ClipName;
  readonly has: Readonly<Partial<Record<ClipName, boolean>>>;
  /** `AFFECT_CHEONGEUN` on the victim (`InstanceBaseEffect.cpp:892-893`). */
  readonly resistsFall?: boolean;
  /** The victim's `isLock` (`ActorInstanceMotion.cpp:472-560`): swinging, fishing, gesturing or in an uncancellable skill. */
  readonly locked?: boolean;
  /** The victim's `IsUsingSkill` (`ActorInstanceMotion.cpp:433-451`). */
  readonly usingSkill?: boolean;
}

/** The one-shot a landed blow starts on its victim and the one-shot queued
 *  behind it, or `clip: null` when the victim's motion does not change. */
export interface HitReaction {
  readonly clip: ClipName | null;
  readonly then: ClipName | null;
}

const NO_CHANGE: HitReaction = { clip: null, then: null };

/** `__ProcessDataAttackSuccess`'s motion half, `__HitGood` and `__HitGreate`
 *  (`ActorInstanceBattle.cpp:605-609,683-698,780-880`): a good blow flinches, a
 *  great one knocks down and queues the stand-up, the flinch and the pair picked
 *  by whether the two bodies face each other. The stun branch is absent: this client plays
 *  no stun as a motion. */
export function hitReaction(input: HitReactionInput): HitReaction {
  // An attack clip converted without a `HittingType` keeps the flinch every
  // landed blow played before the type was carried (`ActorInstanceBattle.cpp:686`).
  const hittingType = input.hittingType ?? HIT_TYPE_GOOD;
  if (hittingType === HIT_TYPE_NONE) return NO_CHANGE; // ActorInstanceBattle.cpp:607-608
  if (isKnockDownClip(input.playing)) return NO_CHANGE; // ActorInstanceBattle.cpp:782-783,825-826
  // The dot of the two facing vectors (`ActorInstanceBattle.cpp:800-804,833-842`)
  // is the cosine of the angle between the headings.
  const scalar = Math.cos(((input.attackerHeading - input.victimHeading) / HEADING_STEPS) * Math.PI * 2);
  if (hittingType === HIT_TYPE_GOOD || input.resistsFall === true) {
    if (input.locked === true) return NO_CHANGE; // ActorInstanceBattle.cpp:806
    if (scalar >= 0 && input.has.damageBack === true) return { clip: "damageBack", then: null }; // ActorInstanceBattle.cpp:813-814
    return input.has.damage === true ? { clip: "damage", then: null } : NO_CHANGE; // ActorInstanceBattle.cpp:808-809,815-816
  }
  if (hittingType !== HIT_TYPE_GREAT) return NO_CHANGE; // ActorInstanceBattle.cpp:694-697
  if (isStandUpClip(input.playing)) return NO_CHANGE; // ActorInstanceBattle.cpp:827-828
  if (input.usingSkill === true) return NO_CHANGE; // ActorInstanceBattle.cpp:846-847
  if (scalar >= 0 && input.has.knockdownBack === true) {
    return { clip: "knockdownBack", then: input.has.standUpBack === true ? "standUpBack" : null }; // :879-882
  }
  if (input.has.knockdown !== true) return NO_CHANGE; // :863,873
  return { clip: "knockdown", then: input.has.standUp === true ? "standUp" : null }; // :865,875
}
